import React, { useCallback, useEffect, useState } from "react";
import { Input } from "../../../../components/Input";
import { PartialNullable } from "../../../../types";
import {
  Contractor,
  DraftAlbum,
  Genre,
  RegionSettings,
} from "../../../../api/useMusicApi";
import { useTranslation } from "../../../../hooks/useTranslation";
import { EditDraftFormLocalizedTitlePart } from "../../draft/DraftFormTitlePart";
import {
  ArtistType,
  DraftFormArtistPart
} from "../../draft/DraftFormArtistPart";
import { InputLabel } from "../../../../components/InputLabel";
import { InputSelect } from "../../../../components/InputSelect";
import { InputCheckbox } from "../../../../components/InputCheckbox";
import { InputMultiSelect } from "../../../../components/InputMultiSelect";
import { UpdateFields } from "../../../../hooks/use-advanced-state";
import { Button } from "../../../../components/Button";
import { FileDropzone } from "../../../../components/Dropzone";
import { useDeepCompareEffect } from "../../../../hooks/use-deep-compare-effect";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import {
  ValidationError,
  ValidationInputWrapper
} from "../../../../components/ValidationInputWrapper";
import { SaveFormButton } from "../../../../components/SaveFormButton";
import { ReactPopover } from "../Tracks/ReactPopover";
import { ImageViewModal } from "../../../../components/Modals/ImageViewModal";

type BaseProps = {
  genres: Array<Genre>;
  contractors: Array<Contractor>;
  onFileSelect: (file: Blob | null) => Promise<any>;
  onSaveAlbum: () => Promise<any>;
};

type AlbumProps = {
  isDraft: boolean;
  entity: PartialNullable<DraftAlbum>;
  onChange: UpdateFields<PartialNullable<DraftAlbum>>;
  validationErrors: ValidationError[];
} & BaseProps;

export const MainAlbumInformation: React.FC<AlbumProps> = ({
                                                             entity,
                                                             genres,
                                                             contractors,
                                                             onChange,
                                                             isDraft,
                                                             onSaveAlbum,
                                                             validationErrors
                                                           }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  useDeepCompareEffect(() => {
    if (uploadedFiles.length != 0) {
      const reader = new FileReader();
      reader.onload = () => {
        // Убираем префикс от base64 кодировки, чтобы бэк нормально считал данные.
        const base64Image = (reader.result as string).replace(/data:image\/png;base64,|data:image\/jpeg;base64,|data:image\/jpg;base64,/, "");
        onChange({ image: base64Image });
      };

      reader.readAsDataURL(uploadedFiles[0]);
    }
  }, [uploadedFiles]);

  useEffect(() => {
    if (contractors.length === 1) {
      onChange({ contractor_id: contractors[0].id });
    }
  }, [contractors.length]);

  const handleDeleteImage = useCallback(() => {
    setUploadedFiles([]);
    onChange({ image_url_template: null, image: null });
  }, []);

  return (
    <>
      <div className={"flex text-sm border-solid py-3 xl:px-8 sm:px-4 border-2 border-gray-200 rounded-r rounded-b"}>
        <div className={"w-full xl:w-9/12 flex-col align-items-center"}>
          <div className={"flex gap-3 mt-4"}>
            <div className={"flex-1"}>
              <div className={"flex gap-3"}>
                <div className={"flex-1"}>
                  <ValidationInputWrapper
                    attribute={"content_type"}
                    validationErrors={validationErrors}
                  >
                    <InputLabel
                      required
                      className={{ label: "text-sm" }}
                      label={`${t("content_type")}`}
                    />
                    <InputSelect
                      required
                      hasError={
                        validationErrors.find((e) => e.attr == "content_type") !=
                        null
                      }
                      label={t("content_type").toString()}
                      disabled={!isDraft}
                      className={"mt-1"}
                      placeholder={`${t("content_type_placeholder")}`}
                      options={[
                        { label: t("audio").toString(), value: "audio" },
                        { label: t("video").toString(), value: "video" }
                      ]}
                      value={entity?.content_type ?? null}
                      onChange={(item) => {
                        onChange((prevState: any) => {
                          return {
                            ...prevState,
                            content_type: item?.value ?? null
                          };
                        });
                      }}
                    />
                  </ValidationInputWrapper>
                </div>
                <div className={"flex-1"} />
              </div>

              <div className={"w-100 mt-2"}>
                <EditDraftFormLocalizedTitlePart
                  entity={entity}
                  validationErrors={validationErrors}
                  disabled={!isDraft}
                  regionSettings={RegionSettings.ALL}
                  onChange={onChange as any}
                />

                <div className={"mt-3"}>
                  <ValidationInputWrapper
                    attribute={"is_explicit"}
                    validationErrors={validationErrors}
                  >
                    <InputCheckbox
                      disabled={!isDraft}
                      label={`${t("explicit_is")}`}
                      checked={entity?.is_explicit ?? false}
                      labelHint={t("is_explicit_hint").toString()}
                      onChange={(value) => onChange({ is_explicit: value })}
                    />
                  </ValidationInputWrapper>
                </div>
              </div>
            </div>
            <div className={"flex flex-col align-items-end justify-end"} style={{ width: "30%" }}>
              <div>
                {
                  ((uploadedFiles.length === 0 && (entity as any)?.image_url_template != null)
                    || entity?.image != null) &&
                  <div className={"mb-3 flex justify-center"} style={{ height: 180, width: 180 }}>
                    <div className={"w-full text-center flex justify-center"}>
                      <ReactPopover trigger={"hover"} content={
                        <div
                          className={
                            "w-full text-muted font-normal flex justify-between text-xs"
                          }
                          style={{ display: "inline-block" }}
                        >
                          <div>{t("click_for_full_view")}</div>
                        </div>
                      }>
                        <img
                          alt={"album_image"}
                          onClick={() => setShowModal(true)}
                          src={uploadedFiles.length === 0 && entity.image_url_template != null
                            ? entity.image_url_template?.replace("{}", "full")
                            : "data:image/png;base64," + entity?.image as string
                          }
                          className={"rounded border hover:opacity-50 h-[180px] cursor-pointer"}
                        />
                      </ReactPopover>

                    </div>
                  </div>
                }
                {!(
                    entity?.image == null &&
                    (entity as any)?.image_url_template == null
                  ) &&
                  isDraft && (
                    <div className={"flex justify-center"}>
                      <Button
                        label={t("delete_image").toString()}
                        icon={faTrash}
                        onClick={handleDeleteImage}
                        style={{ maxWidth: 300 }}
                      />
                    </div>
                  )}
                {entity?.image == null &&
                  (entity as any)?.image_url_template == null &&
                  isDraft && (
                    <div>
                      <FileDropzone
                        title={t("album_image_upload").toString()}
                        maxFiles={1}
                        accept={{
                          "image/png": [".png"],
                          "image/jpeg": [".jpeg", ".jpg"]
                        }}
                        hint={t("upload_album_image_hint").toString()}
                        onFileChange={setUploadedFiles}
                      />
                    </div>
                  )}
              </div>

            </div>
          </div>

          <hr className={"my-4"} />

          <div>
            <div className={"w-full mt-3 flex gap-2"}>
              <div className={"flex-1"}>
                <ValidationInputWrapper
                  isWrap
                  wrapErrorText={t("has_error").toString()}
                  attribute={"country_meta.artists.main"}
                  validationErrors={validationErrors}
                  className={"mb-3 text-md"}
                >
                  <DraftFormArtistPart
                    draft={entity}
                    type={ArtistType.MAIN}
                    disabled={!isDraft}
                    validationErrors={validationErrors}
                    onChange={onChange as any}
                  />
                </ValidationInputWrapper>
              </div>
              <div className={"flex-1"}>
                <ValidationInputWrapper
                  isWrap
                  wrapErrorText={t("has_error").toString()}
                  attribute={"country_meta.artists.featured"}
                  validationErrors={validationErrors}
                >
                  <DraftFormArtistPart
                    type={ArtistType.FEATURED}
                    draft={entity}
                    disabled={!isDraft}
                    validationErrors={validationErrors}
                    onChange={onChange as any}
                  />
                </ValidationInputWrapper>
              </div>
            </div>
          </div>

          <hr className={"my-4"} />

          <div className={"text-sm flex gap-3 my-2"}>
            <div className={"flex-1"}>
              <ValidationInputWrapper
                attribute={"genres"}
                validationErrors={validationErrors}
              >
                <InputMultiSelect
                  required
                  hasError={
                    validationErrors?.find((e) => e.attr === "genres") != null
                  }
                  disabled={!isDraft}
                  max={3}
                  className={"w-full"}
                  labelHint={`${t("genres_and_subgenres_placeholder")}`}
                  label={`${t("genres_and_subgenres_title")}`}
                  placeholder={""}
                  defaultValues={entity?.genres ?? []}
                  options={genres.map((g) => ({ label: g?.title, value: g?.id }))}
                  onChange={(items) => {
                    onChange({
                      genres: items.map((i) => i.value)
                    });
                  }}
                />
              </ValidationInputWrapper>
            </div>
            <div className={"flex-1"}></div>
          </div>

          <div className={"flex gap-3 my-2"}>
            <div className={"flex-1 text-sm"}>
              <ValidationInputWrapper
                attribute={"release_date"}
                validationErrors={validationErrors}
              >
                <Input
                  disabled={!isDraft}
                  label={`${t("album_release_date")}`}
                  validationError={
                    validationErrors?.find((e) => e.attr === "release_date")
                      ?.detail
                  }
                  className={"my-1"}
                  labelHint={t("album_release_date_description").toString()}
                  value={entity?.release_date ?? undefined}
                  onChange={(value) => onChange({ release_date: value })}
                  type={"date"}
                />
              </ValidationInputWrapper>
            </div>
            <div className={"flex-1"}>
              <ValidationInputWrapper
                attribute={"original_release_date"}
                validationErrors={validationErrors}
              >
                <Input
                  disabled={!isDraft}
                  validationError={
                    validationErrors?.find(
                      (e) => e.attr === "original_release_date"
                    )?.detail
                  }
                  labelHint={t("original_release_date_hint").toString()}
                  className={"my-1"}
                  label={`${t("original_album_release_date")}`}
                  value={entity?.original_release_date ?? undefined}
                  onChange={(value) => onChange({ original_release_date: value })}
                  type={"date"}
                />
              </ValidationInputWrapper>
            </div>
          </div>

          <div>
            <div className={"flex gap-3"}>
              <div className={"flex-1"}>
                <ValidationInputWrapper
                  attribute={"code_icpn"}
                  validationErrors={validationErrors}
                >
                  <Input
                    disabled={!isDraft}
                    className={"my-1"}
                    label={`${t("ups")}`}
                    labelHint={t("upc_hint").toString()}
                    defaultValue={entity?.code_icpn ?? ""}
                    onChange={(value) => onChange({ code_icpn: value })}
                  />
                </ValidationInputWrapper>
              </div>
              <div className={"flex-1"}>
                <ValidationInputWrapper
                  attribute={"external_id"}
                  validationErrors={validationErrors}
                >
                  <Input
                    disabled={!isDraft}
                    className={"my-1"}
                    label={`${t("external_id")}`}
                    labelHint={t("external_id_hint").toString()}
                    defaultValue={entity?.external_id ?? ""}
                    onChange={(value) => onChange({ external_id: value })}
                  />
                </ValidationInputWrapper>
              </div>
            </div>

            <div className={"flex w-full my-2 gap-3"}>
              <div className={"flex-1"}>
                <ValidationInputWrapper
                  attribute={"code_grid"}
                  validationErrors={validationErrors}
                >
                  <Input
                    disabled={!isDraft}
                    className={"my-1"}
                    label={`${t("grid")}`}
                    labelHint={t("grid_hint").toString()}
                    defaultValue={entity?.code_grid ?? ""}
                    onChange={(value) => onChange({ code_grid: value })}
                  />
                </ValidationInputWrapper>
              </div>
              <div className={"flex-1"}>
                {contractors.length > 1 && (
                  <>
                    <ValidationInputWrapper
                      attribute={"contractor_id"}
                      validationErrors={validationErrors}
                    >
                      <InputLabel required label={`${t("contractor")}`} />
                      <InputSelect
                        disabled={!isDraft}
                        className={"my-1"}
                        required
                        isClearable
                        placeholder={`${t("choose_contractor")}`}
                        options={contractors.map((c) => ({
                          label: c.title,
                          value: c.id
                        }))}
                        value={entity?.contractor_id ?? null}
                        onChange={(item) =>
                          onChange((prevState: any) => {
                            return {
                              ...prevState,
                              contractor_id: item?.value ?? null
                            };
                          })
                        }
                      />
                    </ValidationInputWrapper>
                  </>
                )}
              </div>
            </div>
          </div>

          {isDraft && (
            <SaveFormButton
              validationErrors={validationErrors}
              onClick={onSaveAlbum}
            />
          )}
        </div>

        {showModal &&
          <ImageViewModal onCancel={() => setShowModal(false)}>
            <div className={"flex justify-center"}>
              <img
                alt={"album_image"}
                onClick={() => setShowModal(true)}
                src={uploadedFiles.length === 0 && entity.image_url_template != null
                  ? entity.image_url_template?.replace("{}", "full")
                  : "data:image/png;base64," + entity?.image as string
                }
                className={"rounded border max-h-[75vh] cursor-pointer"}
              />
            </div>
          </ImageViewModal>}

      </div>
    </>
  );
};
